/** Upload Component - Multi-File Batch Upload with Visual Polish */
import React, { useState, useRef, useCallback } from 'react';
import { extractDocumentWithOllama, checkOllamaAvailable } from './ollamaExtraction.js';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://provider-portal-api-production.gustavdatadc.workers.dev';

// Animated spinner component
const Spinner = ({ size = 20, color = '#fff' }) => (
  <div style={{
    width: size,
    height: size,
    border: `2px solid ${color}30`,
    borderTop: `2px solid ${color}`,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  }} />
);

// File icon with type indicator
const FileIcon = ({ type, status }) => {
  const isPdf = type.toLowerCase().includes('pdf');
  const icon = isPdf ? '📄' : '🖼️';
  const bg = isPdf ? '#e3f2fd' : '#f3e5f5';
  
  return (
    <div style={{
      width: 44,
      height: 44,
      borderRadius: 10,
      background: bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 22,
      position: 'relative',
    }}>
      {icon}
      {status === 'extracting' && (
        <div style={{
          position: 'absolute',
          bottom: -2,
          right: -2,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: '#ff9800',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Spinner size={10} color="#fff" />
        </div>
      )}
    </div>
  );
};

// Status badge with color coding
const StatusBadge = ({ status, isSubmitted }) => {
  const configs = {
    submitted: { bg: '#1565c0', text: '✓ Submitted', icon: '✓' },
    extracted: { bg: '#2e7d32', text: 'Ready', icon: '✓' },
    extracting: { bg: '#ff9800', text: 'Extracting...', icon: '⚡' },
    error: { bg: '#d32f2f', text: 'Failed', icon: '✕' },
    pending: { bg: '#757575', text: 'Queued', icon: '⏳' },
  };
  
  const config = isSubmitted ? configs.submitted : (configs[status] || configs.pending);
  
  return (
    <span style={{
      padding: '6px 14px',
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
      background: config.bg,
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      whiteSpace: 'nowrap',
    }}>
      {config.icon} {config.text}
    </span>
  );
};

// Progress bar component
const ProgressBar = ({ extractingCount, readyCount, total }) => {
  if (total === 0) return null;
  const progress = ((readyCount + extractingCount) / total) * 100;
  
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ 
        height: 6, 
        background: '#e0e0e0', 
        borderRadius: 3,
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #4caf50, #8bc34a)',
          borderRadius: 3,
          transition: 'width 0.3s ease',
        }} />
      </div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        marginTop: 8,
        fontSize: 13,
        color: '#666',
      }}>
        <span>{Math.round(progress)}% complete</span>
        <span>{readyCount} of {total} ready</span>
      </div>
    </div>
  );
};

const UploadComponent = ({ apiToken, onUploadComplete }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [processedFiles, setProcessedFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  const handleFileSelect = useCallback((e) => {
    if (e.target.files?.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  }, []);

  const handleFiles = async (files) => {
    setError(null);
    setShowSuccess(false);
    
    const validFiles = files.filter(file => {
      const isAllowed = file.name.toLowerCase().match(/\.(pdf|png|jpg|jpeg)$/);
      const isSizeOk = file.size <= 10 * 1024 * 1024;
      return isAllowed && isSizeOk;
    });

    if (validFiles.length === 0) {
      setError('Please upload valid PDF or image files (max 10MB each)');
      return;
    }

    if (validFiles.length !== files.length) {
      setError(`${files.length - validFiles.length} file(s) skipped (invalid type or too large)`);
    }

    const queueItems = validFiles.map(file => ({
      id: `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      status: 'pending',
      extractedText: null,
      error: null
    }));

    setUploadQueue(prev => [...prev, ...queueItems]);
    processQueue(queueItems);
  };

  const processQueue = async (items) => {
    setIsProcessing(true);
    
    const available = await checkOllamaAvailable();
    if (!available) {
      setError('AI extraction service not available. Please try again later.');
      setIsProcessing(false);
      return;
    }

    for (const item of items) {
      setUploadQueue(prev => prev.map(q => 
        q.id === item.id ? { ...q, status: 'extracting' } : q
      ));

      try {
        const result = await extractDocumentWithOllama(item.file, apiToken);
        
        if (!result.success) throw new Error(result.error);

        setUploadQueue(prev => prev.map(q => 
          q.id === item.id ? { ...q, status: 'extracted', extractedText: result.rawText } : q
        ));

        setProcessedFiles(prev => [...prev, {
          id: item.id,
          fileName: item.file.name,
          fileSize: item.file.size,
          extractedText: result.rawText,
          submitted: false
        }]);

        await new Promise(r => setTimeout(r, 1000));
      } catch (err) {
        setUploadQueue(prev => prev.map(q => 
          q.id === item.id ? { ...q, status: 'error', error: err.message } : q
        ));
      }
    }

    setIsProcessing(false);
  };

  const handleSubmitAll = async () => {
    const filesToSubmit = processedFiles.filter(f => !f.submitted);
    if (filesToSubmit.length === 0) return;

    setIsSubmitting(true);
    setError(null);

    const results = [];
    
    for (const file of filesToSubmit) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/cases`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiToken}`
          },
          body: JSON.stringify({
            rawText: file.extractedText,
            sourceFile: file.fileName,
            confirmedBy: 'provider',
            submissionType: 'batch_upload'
          })
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        const data = await response.json();
        results.push({ success: true, caseId: data.case?.case_id, fileName: file.fileName });
        
        setProcessedFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, submitted: true, caseId: data.case?.case_id } : f
        ));
      } catch (err) {
        results.push({ success: false, error: err.message, fileName: file.fileName });
      }
    }

    const successCount = results.filter(r => r.success).length;
    
    if (successCount === filesToSubmit.length) {
      setShowSuccess(true);
      onUploadComplete?.();
      setTimeout(() => {
        setUploadQueue([]);
        setProcessedFiles([]);
        setShowSuccess(false);
      }, 3000);
    } else {
      setError(`${filesToSubmit.length - successCount} case(s) failed to submit`);
    }

    setIsSubmitting(false);
  };

  const handleRemoveFile = (id) => {
    setUploadQueue(prev => prev.filter(q => q.id !== id));
    setProcessedFiles(prev => prev.filter(f => f.id !== id));
  };

  const handleClearAll = () => {
    setUploadQueue([]);
    setProcessedFiles([]);
    setError(null);
    setShowSuccess(false);
  };

  const readyCount = processedFiles.filter(f => !f.submitted).length;
  const submittedCount = processedFiles.filter(f => f.submitted).length;
  const extractingCount = uploadQueue.filter(q => q.status === 'extracting').length;
  const errorCount = uploadQueue.filter(q => q.status === 'error').length;
  const totalFiles = uploadQueue.length;

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ 
          fontSize: 48, 
          marginBottom: 12,
          animation: dragActive ? 'bounce 0.5s infinite' : 'none'
        }}>📤</div>
        <h2 style={{ margin: '0 0 8px 0', fontSize: 24, fontWeight: 600 }}>Batch Upload</h2>
        <p style={{ margin: 0, color: '#666', fontSize: 15 }}>
          Drop multiple documents and our AI will extract the data
        </p>
      </div>

      {/* Dropzone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragActive ? '#1976d2' : '#bdbdbd'}`,
          borderRadius: 16,
          padding: '40px 32px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragActive ? '#e3f2fd' : '#fafafa',
          transition: 'all 0.2s ease',
          boxShadow: dragActive ? '0 4px 20px rgba(25, 118, 210, 0.15)' : 'none',
        }}
      >
        <div style={{ fontSize: 56, marginBottom: 16 }}>📁</div>
        <p style={{ margin: '0 0 8px 0', fontSize: 17, fontWeight: 500, color: '#333' }}>
          {dragActive ? 'Drop files here!' : 'Click or drag files here'}
        </p>
        <p style={{ margin: 0, fontSize: 13, color: '#888' }}>
          PDF, PNG, JPG • Up to 10MB each • Multiple files supported
        </p>
        <input 
          ref={fileInputRef} 
          type="file" 
          accept=".pdf,.png,.jpg,.jpeg" 
          onChange={handleFileSelect} 
          style={{ display: 'none' }} 
          multiple
        />
      </div>

      {/* Success Banner */}
      {showSuccess && (
        <div style={{
          marginTop: 20,
          padding: '16px 20px',
          background: 'linear-gradient(135deg, #4caf50, #45a049)',
          borderRadius: 10,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          animation: 'slideIn 0.3s ease',
        }}>
          <span style={{ fontSize: 24 }}>🎉</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>Successfully submitted {submittedCount} cases!</div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>Cases are being processed by AI. Check the Cases tab to review.</div>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && !showSuccess && (
        <div style={{
          marginTop: 20,
          padding: '14px 18px',
          background: '#ffebee',
          borderRadius: 10,
          color: '#c62828',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 14,
        }}>
          <span>⚠️</span>
          {error}
        </div>
      )}

      {/* Queue Section */}
      {(uploadQueue.length > 0 || processedFiles.length > 0) && (
        <div style={{
          marginTop: 28,
          background: 'white',
          borderRadius: 16,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}>
          {/* Queue Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: 18, fontWeight: 600 }}>Upload Queue</h3>
              <p style={{ margin: 0, fontSize: 13, color: '#888' }}>
                {totalFiles} file{totalFiles !== 1 ? 's' : ''} • {readyCount} ready • {submittedCount} submitted
              </p>
            </div>
            
            {/* Status Chips */}
            <div style={{ display: 'flex', gap: 8 }}>
              {extractingCount > 0 && (
                <span style={{
                  padding: '6px 12px',
                  background: '#fff3e0',
                  color: '#e65100',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 500,
                }}>
                  ⚡ {extractingCount} extracting
                </span>
              )}
              {readyCount > 0 && (
                <span style={{
                  padding: '6px 12px',
                  background: '#e8f5e9',
                  color: '#2e7d32',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 600,
                }}>
                  ✓ {readyCount} ready
                </span>
              )}
              {errorCount > 0 && (
                <span style={{
                  padding: '6px 12px',
                  background: '#ffebee',
                  color: '#c62828',
                  borderRadius: 20,
                  fontSize: 12,
                  fontWeight: 500,
                }}>
                  ✕ {errorCount} failed
                </span>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div style={{ padding: '20px 24px 0' }}>
            <ProgressBar 
              extractingCount={extractingCount} 
              readyCount={readyCount} 
              total={totalFiles} 
            />
          </div>

          {/* File List */}
          <div style={{ maxHeight: 350, overflow: 'auto', padding: '0 24px 20px' }}>
            {uploadQueue.map((item, index) => {
              const processedFile = processedFiles.find(f => f.id === item.id);
              const isSubmitted = processedFile?.submitted;
              
              return (
                <div key={item.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '14px 0',
                  borderBottom: index < uploadQueue.length - 1 ? '1px solid #f0f0f0' : 'none',
                  opacity: isSubmitted ? 0.7 : 1,
                }}>
                  <FileIcon type={item.file.name} status={item.status} />
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontWeight: 500, 
                      fontSize: 14,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {item.file.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                      {(item.file.size / 1024).toFixed(0)} KB
                      {processedFile?.caseId && (
                        <span style={{ color: '#1976d2', marginLeft: 8 }}>
                          → Case {processedFile.caseId}
                        </span>
                      )}
                    </div>
                  </div>

                  <StatusBadge status={item.status} isSubmitted={isSubmitted} />

                  {!isSubmitted && item.status !== 'extracting' && (
                    <button 
                      onClick={() => handleRemoveFile(item.id)}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 14,
                        border: 'none',
                        background: '#f5f5f5',
                        color: '#666',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 16,
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#ffebee';
                        e.target.style.color = '#c62828';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f5f5f5';
                        e.target.style.color = '#666';
                      }}
                      title="Remove"
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Action Footer */}
          <div style={{
            padding: '20px 24px',
            borderTop: '1px solid #eee',
            background: '#fafafa',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <button 
              onClick={handleClearAll}
              disabled={isProcessing || isSubmitting}
              style={{
                padding: '10px 20px',
                borderRadius: 8,
                border: '1px solid #ddd',
                background: 'white',
                color: '#666',
                fontSize: 14,
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              Clear All
            </button>
            
            <button 
              onClick={handleSubmitAll}
              disabled={readyCount === 0 || isProcessing || isSubmitting}
              style={{
                padding: '12px 28px',
                borderRadius: 8,
                border: 'none',
                background: readyCount > 0 ? 'linear-gradient(135deg, #4caf50, #45a049)' : '#ccc',
                color: 'white',
                fontSize: 15,
                fontWeight: 600,
                cursor: readyCount > 0 ? 'pointer' : 'not-allowed',
                boxShadow: readyCount > 0 ? '0 4px 12px rgba(76, 175, 80, 0.3)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.2s',
              }}
            >
              {isSubmitting ? (
                <>
                  <Spinner size={16} />
                  Submitting...
                </>
              ) : (
                <>
                  📤 Submit {readyCount > 0 && `(${readyCount})`}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Info Card */}
      <div style={{
        marginTop: 24,
        padding: '18px 20px',
        background: '#f5f7fa',
        borderRadius: 12,
        borderLeft: '4px solid #1976d2',
      }}>
        <div style={{ fontSize: 13, color: '#555', lineHeight: 1.6 }}>
          <strong style={{ color: '#1976d2' }}>💡 How it works:</strong>
          <ol style={{ margin: '8px 0 0 18px', padding: 0 }}>
            <li>Upload one or more medical documents</li>
            <li>AI extracts text from each file automatically</li>
            <li>Click "Submit" to create cases</li>
            <li>Review and edit cases anytime by clicking their Case ID</li>
          </ol>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default UploadComponent;
